import * as Sentry from "@sentry/react";
import { SPAN_STATUS_OK, SPAN_STATUS_ERROR } from "@sentry/core";
import { useCallback, useEffect, useRef, useState } from "react";

const TARGETED_REVIEW_DAY_READY_SPAN_OP = "ui.targeted_review.day_ready";
const TARGETED_REVIEW_SAVE_READY_SPAN_OP = "ui.targeted_review.save_ready";

interface SaveSpan {
  span: Sentry.Span;
  hadErrorAtStart: boolean;
  refreshStarted: boolean;
}

type Outcome = "rendered" | "error" | "abandoned";

const endSpan = ({
  span,
  outcome,
  rowCount,
}: {
  span: Sentry.Span;
  outcome: Outcome;
  rowCount: number;
}) => {
  span.setAttributes({ outcome, row_count: rowCount });
  span.setStatus({
    code: outcome === "error" ? SPAN_STATUS_ERROR : SPAN_STATUS_OK,
  });
  span.end();
};

/**
 * Times what a reviewer waits for: from opening a location and day until its
 * rows are on screen, and from starting a save until the refreshed day is on
 * screen. Both are root spans, so they don't depend on a page-load span.
 */
export const useTargetedReviewLatencySpans = ({
  tenantName,
  location,
  day,
  isFetching,
  isError,
  rowCount,
  hasFreshDataAfterSave,
}: {
  tenantName: string;
  location: string | undefined;
  day: string;
  isFetching: boolean;
  isError: boolean;
  rowCount: number;
  hasFreshDataAfterSave: boolean;
}): {
  trackSave: (save: () => Promise<void>) => Promise<void>;
} => {
  const daySpanRef = useRef<Sentry.Span | null>(null);
  const saveSpanRef = useRef<SaveSpan | null>(null);
  const [completedSaveSpan, setCompletedSaveSpan] =
    useState<Sentry.Span | null>(null);
  const rowCountRef = useRef(rowCount);
  rowCountRef.current = rowCount;

  useEffect(() => {
    if (!location) return;
    const span = Sentry.startInactiveSpan({
      name: "Targeted Review day ready",
      op: TARGETED_REVIEW_DAY_READY_SPAN_OP,
      parentSpan: null,
      attributes: { tenant: tenantName, location, day },
    });
    daySpanRef.current = span;
    return () => {
      if (daySpanRef.current === span) {
        endSpan({ span, outcome: "abandoned", rowCount: rowCountRef.current });
        daySpanRef.current = null;
      }
      if (saveSpanRef.current) {
        endSpan({
          span: saveSpanRef.current.span,
          outcome: "abandoned",
          rowCount: rowCountRef.current,
        });
        saveSpanRef.current = null;
      }
    };
  }, [tenantName, location, day]);

  // Declared after the effect that opens the span so a day served from the
  // client cache, which never fetches, still closes its span on the same commit.
  useEffect(() => {
    const span = daySpanRef.current;
    if (!span || isFetching) return;
    endSpan({ span, outcome: isError ? "error" : "rendered", rowCount });
    daySpanRef.current = null;
  }, [isFetching, isError, rowCount, tenantName, location, day]);

  useEffect(() => {
    const save = saveSpanRef.current;
    if (!save) return;
    if (isFetching) save.refreshStarted = true;
    const { span } = save;
    if (completedSaveSpan !== span || isFetching) return;
    if (isError) {
      if (save.hadErrorAtStart && !save.refreshStarted) return;
      endSpan({ span, outcome: "error", rowCount });
    } else if (hasFreshDataAfterSave) {
      endSpan({ span, outcome: "rendered", rowCount });
    } else {
      return;
    }
    saveSpanRef.current = null;
  }, [completedSaveSpan, hasFreshDataAfterSave, isFetching, isError, rowCount]);

  const trackSave = useCallback(
    async (save: () => Promise<void>): Promise<void> => {
      if (saveSpanRef.current) {
        endSpan({
          span: saveSpanRef.current.span,
          outcome: "abandoned",
          rowCount: rowCountRef.current,
        });
      }
      const span = Sentry.startInactiveSpan({
        name: "Targeted Review save ready",
        op: TARGETED_REVIEW_SAVE_READY_SPAN_OP,
        parentSpan: null,
        attributes: { tenant: tenantName, location, day },
      });
      const saveSpan = {
        span,
        hadErrorAtStart: isError,
        refreshStarted: false,
      };
      saveSpanRef.current = saveSpan;
      try {
        await save();
        if (saveSpanRef.current === saveSpan) {
          setCompletedSaveSpan(span);
        }
      } catch (error) {
        if (saveSpanRef.current === saveSpan) {
          endSpan({ span, outcome: "error", rowCount: rowCountRef.current });
          saveSpanRef.current = null;
        }
        throw error;
      }
    },
    [tenantName, location, day, isError]
  );

  return { trackSave };
};
